import { ProFormField, ProFormGroup, ProFormSelect } from '@ant-design/pro-form';
import { useHover } from 'ahooks';
import { DatePicker } from 'antd';
import { FormInstance } from 'antd/es/form/Form';
import classnames from 'classnames';
import moment, { Moment } from 'moment';
import React, { FC, useEffect, useRef, useState } from 'react';
import { ReportGuideType } from '@/config/app/auth';
import { DATA_TID } from '@/infrastructure/cypress-ids';
import { ReportScene } from '@/infrastructure/reporter/datong/report';
import { CommonDateRange } from '@/infrastructure/utils/timestamp';
import { FormType, TimeRangeCascadeFormType } from '@/presentation/components/form';
import { formatRange, TimeRangeForm } from '@/presentation/components/form/components/time-range';
import {
  FREE_DATE_QUICK, FREE_DATE_RANGE,
  getIssueTimeQuickOptions,
  getTimeQuickOptions,
  getTimeWindowOptions,
  TIME_TYPE_OPTIONS,
} from '@/presentation/components/form/proto';
import FreeProductGuide, { Display, GuideType } from '@/presentation/components/free-product-guide';
import { TdsFormLabel } from '@/presentation/components/tds-form/tds-form-label';
import { useStores } from '@/presentation/store';
import { TimeType } from '@/proto/metric';
import styles from './time-range-cascade.module.scss';

const getTimeQuickInitValue = (timeWindow: TimeType) => {
  const timeQuickInitSetting: Record<string, any> = {
    [TimeType.MINUTE5]: '今天',
    [TimeType.HOUR]: 'custom',
    [TimeType.DAY]: 'custom',
    [TimeType.WEEK]: 'custom',
    [TimeType.MONTH]: 'custom',
  };

  return timeQuickInitSetting[timeWindow];
};

const getTimeQuick = (timeWindow: TimeType) => {
  const timeQuickInitSetting: Record<string, any> = {
    [TimeType.MINUTE5]: '今天',
    [TimeType.HOUR]: '今天',
    [TimeType.DAY]: 'custom',
    [TimeType.WEEK]: '近30天',
    [TimeType.MONTH]: '近90天',
  };

  return timeQuickInitSetting[timeWindow];
};
export const CUSTOM = 'custom';
export const shortRanges: Record<string, any> = {
  ['近1小时']: () => CommonDateRange.hour(),
  ['今天']: () => CommonDateRange.today(),
  ['昨天']: () => CommonDateRange.yesterday(),
  ['近7天']: () => CommonDateRange.lastDaysFull(7),
  ['近30天']: () => CommonDateRange.lastDaysFull(30),
  ['近45天']: () => CommonDateRange.lastDaysFull(45),
  ['近90天']: () => CommonDateRange.lastDaysFull(90),
  ['近180天']: () => CommonDateRange.halfYear(),
  ['custom']: () => CommonDateRange.lastDaysFull(7),
};

export const BUGLY_DATE_RANGE_BY_SHORT_CUT = [
  '近1小时',
  '今天',
  '昨天',
  '近7天',
  '近30天',
  '近45天',
  '近90天',
  '近180天',
];

export const DEFAULT_ANALYTICS_FORMATTER = 'YYYY/MM/DD';

export const DEFAULT_MONITOR_ANALYTICS_LIMIT_DAYS = 179;

export const DEFAULT_ANALYTICS_ACCURATE_DATE = moment().format(DEFAULT_ANALYTICS_FORMATTER);

export const DEFAULT_TIME_RANGE_ANALYTICS_CASCADE = [
  DEFAULT_ANALYTICS_ACCURATE_DATE,
  DEFAULT_ANALYTICS_ACCURATE_DATE,
];
export const getRange = (range: [string, string], format = DEFAULT_ANALYTICS_FORMATTER) => {
  const startDate = moment(range[0]);
  const endDate = moment(range[1]);
  if (!startDate.isValid() || !endDate.isValid()) {
    return undefined;
  }
  return [
    startDate.format(format),
    endDate.format(format),
  ];
};

export const DEFAULT_ISSUE_FORMATTER = 'YYYY-MM-DD HH:mm:ss';

export const DEFAULT_ISSUE_ACCURATE_DATE = moment().format(DEFAULT_ISSUE_FORMATTER);

export const DEFAULT_TIME_RANGE_ISSUE_CASCADE = [
  moment().startOf('date')
    .format(DEFAULT_ISSUE_FORMATTER),
  moment().endOf('date')
    .format(DEFAULT_ISSUE_FORMATTER),
];

export const DEFAULT_TIME_RANGE = [
  moment().subtract(6, 'days')
    .startOf('date'),
  moment().endOf('date'),
];

interface TimeRangeCascadeProps {
  entry: TimeRangeCascadeFormType,
  form: FormInstance,
  defaultValues?: any;
}

export enum TimeRangeGroupName {
  timeQuick = 'timeQuick',
  timeType = 'timeType',
}
const getIssuesLimitDays = (timeWindow: TimeType, isFree: boolean): number => {
  switch (timeWindow) {
    case TimeType.MONTH:
      return isFree ? FREE_DATE_RANGE : 89;
    case TimeType.WEEK:
      return isFree ? FREE_DATE_RANGE : 89;
    case TimeType.DAY:
      return isFree ? FREE_DATE_RANGE : 89; // 可以选时间范围
    case TimeType.MINUTE5:
      // 5分钟聚合粒度，只能选最近2天（今天和昨天）
      return 1; // 可以选时间范围
    case TimeType.HOUR:
      return isFree ? FREE_DATE_RANGE : 13; // 可以选时间范围
    default:
      return 0;
  }
};


export const TimeRangeCascade: FC<TimeRangeCascadeProps> = (props) => {
  const { entry, form } = props;
  const { isFreeProduct } = useStores().ProductStore;
  const isFree = isFreeProduct();
  const { isIssuesTimeWindow, initTimeWindow } = entry;
  const defaultAccurateDate = moment(DEFAULT_ANALYTICS_ACCURATE_DATE);
  const [isRange, setIsRange] = useState<boolean>(true);
  const timeWindowOptions = entry.customTimeWindowOptions
    || getTimeWindowOptions(entry.metricType!, entry.scope, isFree);
  const [timeWindow, setTimeWindow] = useState<TimeType>(initTimeWindow ?? timeWindowOptions[0].value);
  const [disabled, setDisabled] = useState<boolean>();
  const [accurateDate, setAccurateDate] = useState<Moment>(props.defaultValues?.accurateDate || defaultAccurateDate);
  const shift = 0;
  const renameMap = {
    timeWindow: entry.isCompareOpen ? 'timeWindowCompare' : 'timeWindow',
    timeQuick: entry.isCompareOpen ? 'timeQuickCompare' : 'timeQuick',
    accurateDate: entry.isCompareOpen ? 'accurateDateCompare' : 'accurateDate',
    timeType: entry.isCompareOpen ? 'timeTypeCompare' : 'timeType',
  };

  const onChangeTimeWindow = (timeWindow: TimeType) => {
    const timeQuick = getTimeQuickInitValue(timeWindow!);
    setTimeWindow(timeWindow);
    if (isIssuesTimeWindow) {
      if (disabled) {
        return;
      }
      // 修改聚合方式，更新时间
      const quick = getTimeQuick(timeWindow!);
      const timeRange = shortRanges?.[quick!]?.() ?? [];
      form.setFieldsValue({
        [entry.name]: formatRange(timeRange),
        [renameMap.timeQuick]: 'custom',
      });
      setDisabled(false);
      return;
    }
    if (timeQuick !== 'custom') {
      setDisabled(true);
    } else {
      setDisabled(false);
    }

    if (timeWindow === TimeType.MINUTE5 || timeWindow === TimeType.HOUR) {
      setIsRange(false);
      setAccurateDate(moment());
      form.setFieldsValue({
        [renameMap.timeQuick]: timeQuick,
      });
      return;
    }
    setIsRange(true);
    form.setFieldsValue({
      [renameMap.timeQuick]: timeQuick,
      [entry.name]: DEFAULT_TIME_RANGE,
    });
  };

  const onChangeTimeQuick = (timeQuick: string) => {
    if (isIssuesTimeWindow && timeQuick === 'custom') {
      const quick = getTimeQuick(timeWindow!);
      const timeRange = shortRanges?.[quick!]?.() ?? [];
      form.setFieldsValue({
        [entry.name]: formatRange(timeRange),
        [renameMap.timeQuick]: timeQuick,
      });
    } else {
      const timeRange = shortRanges?.[timeQuick!]?.() ?? [];
      const accurateDate = timeRange[0];
      setAccurateDate(moment(accurateDate));
      form.setFieldsValue({
        [entry.name]: formatRange(timeRange),
        [renameMap.timeQuick]: timeQuick,
      });
    }

    if (timeQuick === 'custom') {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  };

  const getLimitDays = (scope: string, timeWindow: TimeType, isFree: boolean) => {
    const fiveMinuteLimitDays = 1;
    const hourLimitDays = 13;
    const limitDaysSetting: Record<string, any> = {
      analytics: {
        [TimeType.MINUTE5]: isFree ? FREE_DATE_RANGE : hourLimitDays,
        [TimeType.HOUR]: isFree ? FREE_DATE_RANGE : hourLimitDays,
        [TimeType.DAY]: isFree ? FREE_DATE_RANGE : entry.limitDays,
      },
      issues: {
        [TimeType.MINUTE5]: fiveMinuteLimitDays,
        [TimeType.HOUR]: isFree ? FREE_DATE_RANGE : hourLimitDays,
        [TimeType.DAY]: isFree ? FREE_DATE_RANGE : entry.limitDays,
      },
    };

    return limitDaysSetting?.[scope]?.[timeWindow] ?? entry.limitDays;
  };

  const disabledDate = (current: Moment) => {
    const limitDays = getLimitDays(entry.scope, timeWindow!, isFree);
    return (!!entry.limitDays
      ? current > moment().endOf('date')
        .subtract(shift, 'days') || current < moment().startOf('date')
        .subtract(limitDays, 'days')
      : current > moment().endOf('date')
        .subtract(shift, 'days'));
  };

  useEffect(() => {
    if (entry.subType && entry.initTimeWindow) {
      setTimeWindow(entry.initTimeWindow!);
      setIsRange(entry.initTimeWindow === TimeType.DAY);
      setAccurateDate(defaultAccurateDate);
    }
  }, [entry.subType]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const {
        timeWindow: fieldsTimeWindow,
        timeWindowCompare: fieldsTimeWindowCompare,
        timeQuick: fieldsTimeQuick,
        timeQuickCompare: fieldsTimeQuickCompare,
      } = form.getFieldsValue();
      const defaultTimeQuick = entry.isCompareOpen ? fieldsTimeQuickCompare : fieldsTimeQuick;
      const defaultTimeWindow = entry.isCompareOpen ? fieldsTimeWindowCompare : fieldsTimeWindow;
      if (entry.hiddenTimeWindow && !entry.isNotRange) {
        setIsRange(true);
      } else {
        if (!isIssuesTimeWindow) {
          setIsRange(defaultTimeWindow === TimeType.DAY);
        }
      }
      setTimeWindow(fieldsTimeWindow);
      if (defaultTimeQuick === 'custom' || entry.isNotRange) {
        setDisabled(false);
      } else {
        onChangeTimeQuick(fieldsTimeQuick);
        setDisabled(true);
      }
    }, 1000);
    return () => {
      clearTimeout(timer);
    };
  }, [entry.isCompareOpen, entry.subType]);

  useEffect(() => {
    setAccurateDate(props.defaultValues?.accurateDate);
  }, [props.defaultValues?.accurateDate]);

  useEffect(() => {
    form.setFieldsValue({
      [renameMap.accurateDate]: accurateDate,
    });
  }, [accurateDate]);

  const updateTimeQuickOptions = (timeWindow: TimeType, isIssuesTimeWindow?: boolean) => (entry.timeQuickOptions
  || entry?.getTimeQuickOptions?.(timeWindow)
    || (isIssuesTimeWindow
      ? getIssueTimeQuickOptions(timeWindow!)
      : getTimeQuickOptions(timeWindow!, entry.scope, entry.hiddenTimeWindow!, entry.showTime!)));

  return <div data-tid={DATA_TID.timeRangeCascade} className={styles.timeRangeCascade}>
      <ProFormGroup>
        {!entry.hiddenTimeWindow ? <ProFormSelect
          name={renameMap.timeWindow}
          options={timeWindowOptions?.map?.(item => ({
            ...item,
            ['data-tid']: `timeWindow-${item.label}`,
          }))}
          className={styles.timeWindow}
          hidden={entry.hiddenTimeWindow}
          initialValue={initTimeWindow ?? timeWindowOptions[0].value}
          allowClear={false}
          fieldProps={{
            dropdownClassName: classnames({
              ['reset-font-size']: true,
              [styles.freeDropdownClass]: isFree,
            }),
            onChange: onChangeTimeWindow,
          }}
        /> : null}
        {!entry.hiddenTimeQuick ? <ProFormSelect
          name={renameMap.timeQuick}
          options={updateTimeQuickOptions(
            timeWindow!,
            isIssuesTimeWindow,
          )?.map?.((item: { label: string, value: string }) => {
            // 限制免费产品
            const openGuide = !FREE_DATE_QUICK.includes(item.value);
            return {
              ...item,
              disabled: openGuide && isFree,
              label: <FreeProductGuide
                  openGuide={openGuide}
                guideType={GuideType.MODAL}
                display={Display.INLINE}
                reportGuideType={ReportGuideType.Modal}
                reportScene={ReportScene.DateCascaderQuick}
                reportField={item.label}
              >
                {item.label}
              </FreeProductGuide>,
              ['data-tid']: `timeQuick-${item.label}`,
            };
          })}
          className={classnames({
            [styles.timeQuick]: !entry.hiddenTimeWindow,
            [styles.hiddenTimeWindowTimeQuick]: entry.hiddenTimeWindow,
          })}
          initialValue={entry?.defaultTimeQuick ?? updateTimeQuickOptions(
            entry.initTimeWindow!,
            isIssuesTimeWindow,
          )?.[0].value}
          allowClear={false}
          fieldProps={{
            dropdownClassName: classnames({
              ['reset-font-size']: true,
              [styles.freeDropdownClass]: isFree,
            }),
            onChange: onChangeTimeQuick,
          }}
        /> : null}
        <TimeRangeForm
          isFreeProduct={isFree}
          quickOptions={updateTimeQuickOptions(
            timeWindow!,
            isIssuesTimeWindow,
          )?.map?.((item: { label: string, value: string }) => ({
            ...item,
            ['data-tid']: `timeQuick-${item.label}`,
          }))}
          timeWindow={timeWindow}
          entry={{
            ...entry,
            type: FormType.TimeRange,
            name: entry.name,
            label: undefined,
            scope: entry.scope,
            limitDays: isIssuesTimeWindow ? getIssuesLimitDays(timeWindow, isFree) : entry.limitDays,
            showTime: entry.showTime,
            ranges: {},
            disabled,
            // dateChange: timeRangeChange,
          }}
          form={form}
          className={classnames({
            [styles.timeRange]: !entry.hiddenTimeWindow,
            [styles.hiddenTimeWindowTimeRange]: entry.hiddenTimeWindow,
          })}
          hidden={!isRange}
        />
        <ProFormField name={renameMap.accurateDate} hidden={isRange}>
          <DatePicker
            allowClear={false}
            className={classnames({ [styles.timeRange]: true, [styles.date]: entry.hiddenTimeType }) }
            defaultValue={defaultAccurateDate}
            disabledDate={disabledDate}
            disabled={disabled}
            value={moment(accurateDate)}
            onChange={(date) => {
              setAccurateDate(date!);
            }}
            format={DEFAULT_ANALYTICS_FORMATTER}
          />
        </ProFormField>
        <ProFormSelect
          name={renameMap.timeType}
          options={(entry.timeTypeOptions || TIME_TYPE_OPTIONS).map(item => ({
            ...item,
            ['data-tid']: `timeType-${item.value}`,
          }))}
          className={classnames({
            [styles.timeType]: true,
            [styles.timeTypeField]: entry.hiddenTimeWindow,
          })}
          initialValue={(entry.timeTypeOptions || TIME_TYPE_OPTIONS)[0].value}
          hidden={entry.hiddenTimeType}
          allowClear={false}
          fieldProps={{
            dropdownClassName: 'reset-font-size',
            showArrow: (entry.timeTypeOptions || TIME_TYPE_OPTIONS)?.length > 1,
          }}
        />
      </ProFormGroup>
  </div>;
};

export const TimeRangeCascadeForm: FC<{
  entry: TimeRangeCascadeFormType,
  form: FormInstance,
  defaultValues?: any;
  hidden?: boolean,
}> = (props) => {
  const { entry, hidden, form, defaultValues } = props;
  const { label, labelTools } = entry;
  const domRef = useRef(null);
  const isHovering = useHover(domRef);

  return <div ref={domRef} className={styles.tdsLabel}><ProFormField
    label={<TdsFormLabel
      isHovering={isHovering}
      label={label}
      tools={labelTools}
    />}
    hidden={hidden}
  >
    <TimeRangeCascade
      entry={entry}
      form={form}
      defaultValues={defaultValues}
    />
  </ProFormField></div>;
};
